import { ApiDrill } from '../services/api.service';

// ── Local drill format used throughout the app ────────────────────────────────
export interface DrillConfig {
  id: number;
  name: string;
  icon: string;
  cue: string;
  setTime: number;
  rounds: number;
  tutorialUrl: string | null;       // media_item.url
  tutorialDuration: number;         // media_item.duration_seconds
  tutorialThumbnail: string | null; // media_item.thumbnail_url
}

// ── Icon mapping by drill name keywords ──────────────────────────────────────
function drillIcon(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('behind'))    return '🔄';
  if (n.includes('crossover')) return '✖';
  if (n.includes('side'))      return '↔';
  return '🏀';
}

// ── Extract the INSTRUCTIONS section from audio_instructions ─────────────────
function extractCue(audio: string | null | undefined): string {
  if (!audio) return '';
  const match = audio.match(/INSTRUCTIONS:\s*(.+?)(?:\.\s*PASSING SCORE|$)/i);
  return match ? match[1].trim() : audio;
}

// ── Replace any duration/rounds numbers baked into the instructions text with
//    the REAL values from getDrills() (set_time / rounds) — the backend's
//    audio_instructions copy can be stale (e.g. "for 60 seconds... repeat 3
//    times") even though set_time/rounds are the source of truth. ───────────
function buildCue(audio: string | null | undefined, setTime: number, rounds: number): string {
  let cue = extractCue(audio);
  if (!cue) return '';
  cue = cue.replace(/for\s+\d+(\.\d+)?\s*seconds?/i, `for ${setTime} second${setTime === 1 ? '' : 's'}`);
  cue = cue.replace(/repeat\s+\d+\s*times?/i, `repeat ${rounds} time${rounds === 1 ? '' : 's'}`);
  cue = cue.replace(/\b\d+\s*rounds?\b/i, `${rounds} round${rounds === 1 ? '' : 's'}`);
  return cue;
}

// ── Clean display name: remove "(Booth)" suffix ───────────────────────────────
function cleanName(name: string): string {
  return name.replace(/\s*\(Booth\)\s*/i, '').trim();
}

// ── Map API drills → DrillConfig[] ───────────────────────────────────────────
export function mapApiDrills(apiDrills: ApiDrill[]): DrillConfig[] {
  return apiDrills.map(d => ({
    id:               d.id,
    name:             cleanName(d.name),
    icon:             drillIcon(d.name),
    cue:              buildCue(d.audio_instructions, d.set_time, d.rounds),
    setTime:          d.set_time,
    rounds:           d.rounds,
    tutorialUrl:      d.media_item?.url ?? null,
    tutorialDuration: d.media_item?.duration_seconds ?? 10,
    tutorialThumbnail:d.media_item?.thumbnail_url ?? null,
  }));
}
